import { Component, EventEmitter, Output, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

export enum PaymentTypeEnum {
  CHEQUE = "cheque",
  CASH = "cash",
  CREDIT = "credit",
  ONLINE = "online"
}

export enum PaymentStatusEnum {
  PAID = "paid",
  PENDING = "pending",
  FAILED = "failed",
  IN_PROGRESS = "in_progress"
}

interface InvoiceItem {
  product_id?: string;
  name: string;
  qty: number;
  price: number;
}

@Component({
  selector: 'app-create-invoice-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './create-invoice-modal.component.html',
  styleUrls: ['./create-invoice-modal.component.scss']
})
export class CreateInvoiceModalComponent implements OnInit {
  setMode(mode: 'create' | 'upload') {
    this.mode = mode;
  }

  onRetailerDropdownBlur(event: FocusEvent) {
    // Only close if focus moves outside the dropdown/input
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (!relatedTarget || !relatedTarget.classList.contains('dropdown-item')) {
      this.showRetailerDropdown = false;
    }
  }
  mode: 'create' | 'upload' = 'upload';
  uploadAmount: number = 0;
  uploadFile: File | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() submitInvoice = new EventEmitter<any>();

  @Input() isEdit: boolean = false;
  @Input() invoiceData: any = null;

  paymentTypes = Object.values(PaymentTypeEnum);
  paymentStatuses = Object.values(PaymentStatusEnum);

  invoice = {
    distributor_id: '',
    retailer_id: '',
    order_date: '',
    due_date: '',
    amount: 0,
    amount_paid: 0,
    items: [] as InvoiceItem[],
    payment_type: PaymentTypeEnum.CASH,
    payment_status: PaymentStatusEnum.PENDING,
    is_consent: false,
    terms_and_conditions_accepted: false,
    invoice_id: '',
    invoice_no: ''
  };

  retailers: any[] = [];
  filteredRetailers: any[] = [];
  retailerSearchTerm = '';
  showRetailerDropdown = false;
  selectedRetailerName = '';
  selectedFileName: string = '';
  submitting = false;
  errorMsg = '';
  fieldErrors: { [key: string]: string } = {};
  userType: string = '';

  constructor(private http: HttpClient) { }

  ngOnInit() {
    this.loadUserType();
    this.loadDistributorId();
    this.fetchRetailers();
    if (this.isEdit && this.invoiceData) {
      this.mode = 'create';
      this.populateEditData();
    }
  }

  loadUserType() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        this.userType = user.user_type?.toLowerCase() || '';
      } catch { }
    }
  }

  populateEditData() {
    this.invoice = {
      ...this.invoice,
      distributor_id: this.invoiceData.distributor_id,
      retailer_id: this.invoiceData.retailer_id,
      order_date: this.invoiceData.order_date ? this.invoiceData.order_date.split('T')[0] : '',
      due_date: this.invoiceData.due_date ? this.invoiceData.due_date.split('T')[0] : '',
      amount: this.invoiceData.amount,
      amount_paid: this.invoiceData.amount_paid,
      items: this.invoiceData.items || [],
      payment_type: this.invoiceData.payment_type,
      payment_status: this.invoiceData.payment_status,
      terms_and_conditions_accepted: true, // Assuming already accepted if editing
      invoice_id: this.invoiceData.invoice_id,
      invoice_no: this.invoiceData.invoice_no
    };
    this.selectedRetailerName = this.invoiceData.retailer?.retailer_name || this.invoiceData.retailer_name || '';
    this.retailerSearchTerm = this.selectedRetailerName;
  }

  loadDistributorId() {
    // In edit mode, preserve the invoice's existing distributor_id
    if (this.isEdit && this.invoiceData?.distributor_id) {
      this.invoice.distributor_id = this.invoiceData.distributor_id;
      return;
    }
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        this.invoice.distributor_id = user.user_id || '';
      } catch { }
    }
  }


  fetchRetailers() {
    // Admins/super_admins use the admin retailers endpoint; distributors use their own
    const isAdmin = this.userType === 'admin' || this.userType === 'super_admin';
    const url = isAdmin
      ? `${environment.apiBaseUrl}/admin/retailers?limit=1000`
      : `${environment.apiBaseUrl}/distributor/retailers?limit=1000`;

    this.http.get<any>(url).subscribe({
      next: (res) => {
        if (res.statusCode === 200 && res.data) {
          // Admin endpoint returns res.data.retailers, distributor endpoint returns res.data.retailers too
          this.retailers = res.data.retailers || res.data || [];
          this.filteredRetailers = this.retailers;
        }
      },
      error: () => {
        // Handle error silently
      }
    });
  }

  searchRetailers(term: string) {
    this.showRetailerDropdown = true;
    if (!term || term.trim().length === 0) {
      this.filteredRetailers = this.retailers;
    } else {
      const lowerTerm = term.toLowerCase();
      this.filteredRetailers = this.retailers.filter(r =>
        r.retailer_name.toLowerCase().includes(lowerTerm) ||
        r.email.toLowerCase().includes(lowerTerm)
      );
    }
  }

  selectRetailer(retailer: any) {
    this.invoice.retailer_id = retailer.id;
    this.selectedRetailerName = retailer.retailer_name;
    this.retailerSearchTerm = retailer.retailer_name;
    this.showRetailerDropdown = false;
  }

  addItem() {
    this.invoice.items.push({
      name: '',
      qty: 1,
      price: 0
    });
  }

  removeItem(index: number) {
    if (this.invoice.items.length > 1) {
      this.invoice.items.splice(index, 1);
      this.calculateAmount();
    }
  }

  calculateAmount() {
    // Only auto-calculate when every item has a positive price filled in.
    // If prices are empty/zero, keep whatever amount the user manually entered.
    const allItemsHavePrice = this.invoice.items.length > 0 &&
      this.invoice.items.every(item => item.price != null && item.price > 0);
    if (allItemsHavePrice) {
      this.invoice.amount = this.invoice.items.reduce((sum, item) => sum + (item.qty * item.price), 0);
    }
  }

  onItemChange() {
    this.calculateAmount();
  }

  validate() {
    this.fieldErrors = {};

    // Retailer
    if (!this.invoice.retailer_id) {
      this.fieldErrors['retailer_id'] = 'Retailer is required.';
    }

    // Order Date
    if (!this.invoice.order_date) {
      this.fieldErrors['order_date'] = 'Order Date is required.';
    }

    // Due Date
    if (!this.invoice.due_date) {
      this.fieldErrors['due_date'] = 'Due Date is required.';
    } else if (new Date(this.invoice.due_date) < new Date(this.invoice.order_date)) {
      this.fieldErrors['due_date'] = 'Due Date must be after Order Date.';
    }

    // Amount
    if (!this.invoice.amount || this.invoice.amount <= 0) {
      this.fieldErrors['amount'] = 'Amount must be greater than 0.';
    }

    // Amount Paid
    if (this.invoice.amount_paid < 0) {
      this.fieldErrors['amount_paid'] = 'Amount Paid cannot be negative.';
    }
    if (this.invoice.amount_paid > this.invoice.amount) {
      this.fieldErrors['amount_paid'] = 'Amount Paid cannot exceed Total Amount.';
    }

    // Items — at least one required for new invoices; optional for edits
    if (!this.isEdit && this.invoice.items.length === 0) {
      this.fieldErrors['items'] = 'At least one item is required.';
    } else {
      this.invoice.items.forEach((item, index) => {
        if (!item.name || item.name.trim().length === 0) {
          this.fieldErrors[`item_name_${index}`] = 'Item name is required.';
        }
        if (!item.qty || item.qty <= 0) {
          this.fieldErrors[`item_qty_${index}`] = 'Quantity must be greater than 0.';
        }
        // price is optional — 0 or empty is acceptable
      });
    }

    // Payment Type
    if (!this.invoice.payment_type) {
      this.fieldErrors['payment_type'] = 'Payment Type is required.';
    }

    // Terms and Conditions — only required for new invoices, not edits
    if (!this.isEdit && !this.invoice.terms_and_conditions_accepted) {
      this.fieldErrors['terms_and_conditions_accepted'] = 'You must accept the terms and conditions.';
    }

    return Object.keys(this.fieldErrors).length === 0;
  }


  onSubmit() {
    this.errorMsg = '';
    if (this.mode === 'create') {
      // Only auto-recalculate amount from items for new invoices.
      // In edit mode, respect whatever amount the admin has entered.
      if (!this.isEdit) {
        this.calculateAmount();
      }
      if (!this.validate()) {
        this.errorMsg = 'Please fix the errors above.';
        return;
      }
      this.submitting = true;
      this.submitInvoice.emit({ mode: this.isEdit ? 'edit' : 'create', data: this.invoice });
    } else if (this.mode === 'upload') {
      this.fieldErrors = {};
      if (!this.uploadFile) {
        this.fieldErrors['upload_file'] = 'Invoice file is required.';
      }
      if (Object.keys(this.fieldErrors).length > 0) {
        this.errorMsg = 'Please fix the errors above.';
        return;
      }
      this.submitting = true;
      // Prepare FormData for upload — only distributor_id + file
      const formData = new FormData();
      formData.append('distributor_id', this.invoice.distributor_id);
      formData.append('file', this.uploadFile!);
      this.submitInvoice.emit({ mode: 'upload', data: formData });
    }
  }

  onFileChange(event: any) {
    const file = event.target.files && event.target.files[0];
    if (file) {
      this.uploadFile = file;
      this.selectedFileName = file.name;
    }
  }

  resetSubmitting() {
    this.submitting = false;
  }

  onClose() {
    this.close.emit();
  }


  removeFile(e: any) {
    e.stopPropagation(); // prevent reopening file picker

    this.selectedFileName = "";

    // reset actual file input
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }

    // optional: reset model / backend payload
    this.uploadFile = null;

    console.log('File removed');
  }
}
